import { useAuth } from '@hostesa/auth-client';
import { useFlags } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import { AppShell, Button, type NavGroup } from '@hostesa/ui';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  BedDouble,
  BookOpen,
  CalendarCheck,
  ConciergeBell,
  Gauge,
  Globe,
  Hotel,
  ListChecks,
  LogOut,
  MessagesSquare,
  Phone,
  PhoneCall,
  PhoneForwarded,
  Plug,
  ScrollText,
  Settings,
  SlidersHorizontal,
} from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useHotelContext } from '../lib/hotel-context';
import { ReceptionAside } from '../routes/reception/reception-page';
import { CommandPalette, CommandPaletteTrigger } from './command-palette';
import { HotelSwitcher } from './hotel-switcher';
import { NAV_GROUPS } from './nav-config';

/**
 * Per-item nav glyphs. Thin-line (1.3 stroke), 24-grid, never filled — matching
 * the brand iconography spec (Brand Book ch. 07). Keyed by the nav item id.
 */
const ICON_PROPS = { className: 'size-4 shrink-0', strokeWidth: 1.3, 'aria-hidden': true } as const;

/**
 * Up-to-two-letter initials for the sidebar avatar. Prefers the first letters
 * of a two-part display name (e.g. "Ada Lovelace" → "AL"); falls back to the
 * first letter of the name or email, and to "·" when neither is present.
 */
function initialsOf(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name ?? email ?? '').trim();
  if (!source) return '·';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}
const NAV_ICONS: Record<string, ReactNode> = {
  reception: <ConciergeBell {...ICON_PROPS} />,
  'live-calls': <PhoneCall {...ICON_PROPS} />,
  calls: <Phone {...ICON_PROPS} />,
  conversations: <MessagesSquare {...ICON_PROPS} />,
  reservations: <CalendarCheck {...ICON_PROPS} />,
  bookings: <BedDouble {...ICON_PROPS} />,
  tasks: <ListChecks {...ICON_PROPS} />,
  knowledge: <BookOpen {...ICON_PROPS} />,
  onboarding: <Globe {...ICON_PROPS} />,
  analytics: <BarChart3 {...ICON_PROPS} />,
  usage: <Gauge {...ICON_PROPS} />,
  'hotel-setup': <SlidersHorizontal {...ICON_PROPS} />,
  'hotel-profile': <Hotel {...ICON_PROPS} />,
  scripts: <ScrollText {...ICON_PROPS} />,
  'handoff-rules': <PhoneForwarded {...ICON_PROPS} />,
  integrations: <Plug {...ICON_PROPS} />,
  settings: <Settings {...ICON_PROPS} />,
};

/**
 * Hotel-scoped layout: the brand AppShell + SidebarNav, with nav items gated by
 * RBAC permissions (`can()`) and feature flags. Renders the active hotel's
 * pages via `<Outlet/>`. Slug resolution + the RBAC/ABAC guard have already run
 * in the route `beforeLoad` before this component mounts.
 */
export function HotelLayout() {
  const { t } = useT('platform');
  const { can, user, logout } = useAuth();
  const flags = useFlags();
  const hotel = useHotelContext();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const base = `/${hotel.tenantSlug}/${hotel.hotelSlug}`;
  const isReception = pathname === base || pathname === `${base}/reception`;
  const userInitials = initialsOf(user?.fullName, user?.email);

  const navGroups = useMemo<NavGroup[]>(() => {
    return NAV_GROUPS.map((group) => ({
      id: group.id,
      label: t(group.labelKey),
      items: group.items
        // RBAC: hide items the active role cannot use.
        .filter((item) => can(item.permission))
        // Feature flags: hide flagged-off surfaces.
        .filter((item) => (item.flag ? flags[item.flag] : true))
        .map((item) => {
          const href = `${base}/${item.segment}`;
          return {
            id: item.id,
            label: t(item.labelKey),
            icon: NAV_ICONS[item.id],
            href,
            active: pathname === href || pathname.startsWith(`${href}/`),
            // No fabricated counts — a badge appears only when a real count query
            // is wired (none yet). See spec 44 (dashboard truth).
            onClick: (() => {}) as () => void,
          };
        }),
    })).filter((group) => group.items.length > 0);
  }, [t, can, flags, base, pathname]);

  // Intercept nav clicks for client-side routing (hrefs keep links real for a11y).
  const navGroupsWithHandlers = useMemo<NavGroup[]>(
    () =>
      navGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          onClick: () => {
            if (item.href) void navigate({ to: item.href });
          },
        })),
      })),
    [navGroups, navigate],
  );

  return (
    <>
      <CommandPalette />
      <AppShell
        data-testid="platform-shell"
        navGroups={navGroupsWithHandlers}
        aside={isReception ? <ReceptionAside /> : undefined}
        asideTriggerLabel={t('shell.liveCall')}
        asideTitle={t('shell.liveCall')}
        mobileNavLabel={t('shell.openNav')}
        logo={
          <span className="flex items-center gap-2.5">
            <img src="/favicon.svg" alt="" className="h-7 w-7" />
            <span className="font-[var(--font-serif)] text-lg text-[var(--color-charcoal)]">
              {t('appName')}
            </span>
          </span>
        }
        topBar={{
          leading: (
            <div className="flex items-center gap-2">
              <HotelSwitcher />
              <CommandPaletteTrigger />
            </div>
          ),
          statusLine: `${t('shell.timezone')}: ${hotel.timezone}`,
          trailing: (
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={() => logout()}>
              <LogOut className="size-4" strokeWidth={1.3} aria-hidden="true" />
              {t('auth.signOut', { ns: 'common' })}
            </Button>
          ),
        }}
        sidebarFooter={
          <div
            data-testid="sidebar-user"
            className="flex items-center gap-2.5 text-xs text-[var(--color-stone-mid)]"
          >
            <span
              data-testid="sidebar-user-avatar"
              aria-hidden="true"
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-charcoal)] font-medium text-[0.7rem] text-[var(--color-alabaster)]"
            >
              {userInitials}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-medium text-[var(--color-charcoal)]">
                {user?.fullName ?? user?.email}
              </span>
              <span className="truncate">{hotel.tenantName}</span>
            </span>
          </div>
        }
      >
        <Outlet />
      </AppShell>
    </>
  );
}
